import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CLIENT_FIELDS, inputCls } from "./utils";

// Add-client modal — the "manual" entry path when someone lands on a
// board without going through the wizard (e.g. bulk phone signups,
// staff onboarding a partner-invited customer by phone).

export function ClientModal({ board, isPartnerView, onClose, onSaved }) {
  const [form, setForm] = useState({ stage: board.stages?.[0]?.key || "" });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const url = isPartnerView ? "/partner/migration/clients" : `/commissions/migrations/${board.partner_code}/clients`;
      await api.post(url, form);
      toast.success("Client added");
      onSaved();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="client-modal">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display text-xl tracking-tight">Add client</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CLIENT_FIELDS.map(f => (
            <label key={f.key} className={`block ${f.multiline ? "md:col-span-2" : ""}`}>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">{f.label}</span>
              {f.multiline ? (
                <textarea
                  data-testid={`client-field-${f.key}`}
                  rows={3} value={form[f.key] || ""}
                  onChange={(e) => setForm({...form, [f.key]: e.target.value})}
                  className={inputCls}
                />
              ) : (
                <input
                  data-testid={`client-field-${f.key}`}
                  type={f.type || "text"} value={form[f.key] || ""}
                  onChange={(e) => setForm({...form, [f.key]: e.target.value})}
                  className={inputCls}
                />
              )}
            </label>
          ))}
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Stage</span>
            <select
              data-testid="client-field-stage"
              value={form.stage} onChange={(e) => setForm({...form, stage: e.target.value})}
              className={inputCls}
            >
              {board.stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm">Cancel</button>
            <button
              data-testid="client-modal-save"
              type="submit" disabled={busy}
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
